// Libs
import React, { useState, useCallback, useContext } from 'react';
// Utils
import GENERAL from 'utils/constants/general';
import { debounce, filterOption } from 'utils/libs';
import ModalBuilderConfig from 'core/ModalBuilder/modal.config';
import { getValueFromRowData } from 'components/DataFieldManager/OnlyReadField/OnlyReadField';
import OrderAuthorization from 'modules/Orders/Authorization';
// Context
import { ModalBuilderContext } from 'core/context';
// Hooks
import { useOrderEventAssignment } from 'hooks';
import { MAX_BULK_EVENT_ORDERS } from 'hooks/Orders/useOrderAssignments.utils';
import { useSelector } from 'react-redux';
import useTheme from 'hooks/useTheme';
// Components
import { Wrapper, LogoIcon, Select, Span } from 'components';
import Updating from '../Updating';
import Status from '../Status';
// Selectors
import { selectProfile } from 'screens/Login/selectors';
// Styles
import styles from './EventId.module.css';

const { UNSUCCESS } = GENERAL.ENV.STATUS;
const componentStyle = {
	select: {
		dark: styles.containerSelectDark,
		light: styles.containerSelectLight,
		disabled: styles.containerSelectDisabled,
	},
	dropdown: {
		dark: styles.containerDropdownDark,
		light: styles.containerDropdownLight,
		disabled: styles.containerDropdownDisabled,
	},
};

const getEventsWithCurrentEvent = (currentEvent, events) => {
	const hasCurrentEvent = !!events.find(e => e.id === currentEvent.id);
	return !hasCurrentEvent && currentEvent.id
		? [currentEvent, ...events]
		: events;
};
const Selection = ({
	state,
	field,
	rowData,
	onGetEventAssignment,
	onSetEventAssignment,
	style,
	isAuthorized,
	placeholder,
	disabledReason,
}) => {
	const { selectedEventId, isLoading, data: events } = state;
	const { themeProfile } = useTheme();
	const { setModal } = useContext(ModalBuilderContext);
	const [isOpen, setIsOpen] = useState(false);

	const fetchDebouncedEventAssignment = useCallback(
		debounce(onGetEventAssignment, 200),
		[],
	);

	const onChange =
		field.onChange ||
		function (props) {
			setModal(
				ModalBuilderConfig.getAuthorizedModalKeys().updateOrderEvent,
				props,
			);
		};

	const rows = Array.isArray(rowData) ? rowData : [rowData];
	const isBulk = rows.length > 1;
	const firstRow = rows[0];
	const currentEventValue = isBulk
		? undefined
		: getValueFromRowData({ field, rowData: firstRow });
	const currentEvent = {
		id: isBulk ? undefined : firstRow[field.id],
		name: isBulk
			? undefined
			: (field.uiValueProp && currentEventValue) ||
				firstRow.eventName ||
				field.uiValue ||
				currentEventValue,
	};
	const _events = !isOpen
		? events
		: getEventsWithCurrentEvent(currentEvent, events);

	const getClassName = () => {
		if (!isAuthorized || disabledReason) return componentStyle[style].disabled;
		return componentStyle[style][themeProfile];
	};

	return (
		<div className={getClassName()} title={disabledReason}>
			<Select
				width='100%'
				filterOption={filterOption}
				value={
					!_events.length || isBulk
						? undefined
						: selectedEventId || currentEvent.id
				}
				placeholder={placeholder || currentEvent.name}
				showSearch
				onSearch={searchValue => fetchDebouncedEventAssignment({ searchValue })}
				onChange={(_value, { props }) => {
					if (!isBulk && _value === currentEvent.id) return;
					onChange({
						field,
						rowData,
						option: {
							id: _value,
							name: props.children,
							props: props.eventProps,
						},
						isSetting: state.isSetting,
						onSetEventAssignment,
					});
				}}
				onDropdownVisibleChange={visible => {
					if (visible) onGetEventAssignment();
					setIsOpen(visible);
				}}
				dropdownRender={menu => (
					<>
						{menu}
						{isLoading && (
							<Wrapper width='100%' justifyContent='center'>
								<LogoIcon spin={true} />
							</Wrapper>
						)}
					</>
				)}
				disabled={!isAuthorized || !!disabledReason}
			>
				{_events.map(event => (
					<Select.Option
						key={event.id}
						value={event.id}
						eventProps={event.props}
					>
						{event.name}
					</Select.Option>
				))}
			</Select>
		</div>
	);
};

const EventId = ({
	rowData,
	field,
	style = 'select',
	placeholder,
	maxBulkOrders = MAX_BULK_EVENT_ORDERS,
}) => {
	const { isDarkTheme } = useTheme();
	const profile = useSelector(selectProfile);
	const { state, onGetEventAssignment, onRetry, onSetEventAssignment } =
		useOrderEventAssignment();
	const isAuthorized = new OrderAuthorization(profile).checkEditOrderEvent();
	const selectedCount = Array.isArray(rowData) ? rowData.length : 1;
	const disabledReason =
		selectedCount > maxBulkOrders
			? `Puedes cambiar el evento de hasta ${maxBulkOrders} órdenes a la vez`
			: undefined;

	if (state.status === UNSUCCESS)
		return (
			<Status
				className={isDarkTheme ? styles.containerDark : styles.containerLight}
				message={state.message}
				onRetry={onRetry}
			/>
		);

	if (state.isSetting)
		return (
			<Updating
				className={isDarkTheme ? styles.containerDark : styles.containerLight}
			/>
		);

	return style === 'dropdown' ? (
		<Selection
			state={state}
			field={field}
			rowData={rowData}
			onGetEventAssignment={onGetEventAssignment}
			onSetEventAssignment={onSetEventAssignment}
			style={style}
			isAuthorized={isAuthorized}
			placeholder={placeholder}
			disabledReason={disabledReason}
		/>
	) : style === 'select' && !isAuthorized ? (
		<Span>{rowData.eventName}</Span>
	) : (
		<Selection
			state={state}
			field={field}
			rowData={rowData}
			onGetEventAssignment={onGetEventAssignment}
			onSetEventAssignment={onSetEventAssignment}
			style={style}
			isAuthorized={isAuthorized}
			placeholder={placeholder}
			disabledReason={disabledReason}
		/>
	);
};

export default EventId;
